import { useMemo, useState } from "react";

import type { RemsStatusDto } from "@/api/types";
import { IconButton } from "@/components/Button/IconButton";
import { RefreshCcw01Icon } from "@/components/Icons/RefreshCcw01Icon";
import { RemsStatus } from "@/components/Ticket/RemsStatus";
import { Button } from "@/design-system/Button";
import { Card } from "@/design-system/Card";
import { Inline } from "@/design-system/Inline";
import { Select } from "@/design-system/Select";
import { Spinner } from "@/design-system/Spinner";
import { Spread } from "@/design-system/Spread";
import { Stack } from "@/design-system/Stack";
import { Text } from "@/design-system/Text";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTranslation } from "@/translations";

export function TicketRemsCard({
  ticketId,
  ticketIsCollective,
  rems,
}: {
  ticketId: string;
  ticketIsCollective: boolean;
  rems: RemsStatusDto;
}): React.ReactNode {
  const { t } = useTranslation();

  const contracts = useMemo(() => {
    if (ticketIsCollective) {
      return [
        {
          id: undefined,
          collective: true,
          description: t("page.tickets.rems.contracts.contract-collective"),
        },
        ...rems.contracts,
      ];
    }

    return rems.contracts;
  }, [rems.contracts, t, ticketIsCollective]);

  const [selectedContract, setSelectedContract] = useState(contracts[0]);
  if (!selectedContract && contracts.length) {
    setSelectedContract(contracts[0]);
  }

  const uploadTicketMutation = ticketMutations.useUploadTicketToRems(ticketId);
  const syncTicketMutation = ticketMutations.useSyncTicketRems(ticketId);

  return (
    <Card>
      <RemsStatus bigLogo rems={rems} isLoading={uploadTicketMutation.isPending || syncTicketMutation.isPending} />
      {rems.state === "errorGeneric" ? rems.errorDetails : null}
      {!rems.externalId && (rems.state === "ready" || rems.contracts.length > 0) && (
        <>
          {syncTicketMutation.isPending && (
            <section className="my-space-xs flex justify-center">
              <Spinner size="sm" />
            </section>
          )}
          {!syncTicketMutation.isPending && (
            <Stack gap="md">
              <Stack gap="xs">
                <Spread space="sm" align="baseline">
                  <Text as="label" htmlFor="rems-contract" variant="caption" tone="secondary" overflow="normal">
                    {t("page.tickets.rems.contracts.title")}
                  </Text>
                  <IconButton
                    data-testid="rems-refresh-contracts"
                    title={t("page.tickets.rems.actions.refresh-contracts")}
                    onClick={() => syncTicketMutation.mutate()}
                    disabled={uploadTicketMutation.isPending}
                    icon={<RefreshCcw01Icon />}
                  />
                </Spread>
                <Select
                  id="rems-contract"
                  items={contracts}
                  placeholder={t("page.tickets.rems.actions.no-contracts")}
                  keySelector={(x) => x.id || "collective"}
                  renderOption={(x) => x.description}
                  onChange={setSelectedContract}
                  selected={selectedContract}
                  disabled={uploadTicketMutation.isPending}
                />
              </Stack>
              <Inline justify="center">
                <Button
                  data-testid="rems-upload-ticket"
                  isLoading={uploadTicketMutation.isPending}
                  onClick={() =>
                    uploadTicketMutation.mutate(
                      "collective" in selectedContract && selectedContract.collective
                        ? { sendAsCollective: true }
                        : { contractId: selectedContract.id, sendAsCollective: false },
                    )
                  }
                >
                  {t("page.tickets.rems.actions.send-ticket")}
                </Button>
              </Inline>
            </Stack>
          )}
        </>
      )}
      {rems.state === "errorGeneric" ||
      rems.state === "errorNoUserFound" ||
      rems.state === "errorNoContracts" ||
      rems.state === "notStarted" ? (
        <Inline justify="center">
          <Button
            data-testid="rems-sync-ticket"
            variant="soft"
            isLoading={syncTicketMutation.isPending}
            onClick={() => syncTicketMutation.mutate()}
          >
            {t("page.tickets.rems.actions.sync")}
          </Button>
        </Inline>
      ) : null}
    </Card>
  );
}
